import { useEffect, useMemo } from 'react';
import { createFileRoute, Link, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { BarChart3, Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  DEFAULT_INVOICE_LIST_FILTERS,
  INVOICE_STATUS_FILTERS,
  InvoiceListPanel,
  type InvoiceListFilters,
} from '@/components/organisms/invoices/InvoiceListPanel';
import { InvoiceTableView } from '@/components/organisms/invoices/InvoiceTableView';
import { EntityIndexLayout, WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { useEntityRouteState } from '@/hooks/navigation/useEntityRouteState';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useHasPermission, useIsFieldHidden } from '@/hooks/usePermissions';

const NUMBER_FILTER_OPS = ['eq', 'gt', 'lt', 'gte', 'lte'] as const;

/**
 * `displayStatus` is the invoice list API's name for the Clearing and Unpaid
 * filters, and what a link built from an API query carries. The page accepts
 * it as an alias for `status` and rewrites the URL to `status=` (see
 * `InvoicesLayout`); any other value is ignored.
 */
const STATUS_BY_DISPLAY_STATUS: Record<string, InvoiceListFilters['status']> = {
  CLEARING: 'clearing',
  UNPAID: 'unpaid',
};

function statusFromSearch(search: Record<string, unknown>): InvoiceListFilters['status'] {
  if (INVOICE_STATUS_FILTERS.some((filter) => filter.value === search.status)) {
    return search.status as InvoiceListFilters['status'];
  }
  const alias =
    typeof search.displayStatus === 'string'
      ? STATUS_BY_DISPLAY_STATUS[search.displayStatus.toUpperCase()]
      : undefined;
  return alias ?? DEFAULT_INVOICE_LIST_FILTERS.status;
}

export const Route = createFileRoute('/_dashboard/dashboard/invoices')({
  validateSearch: (search: Record<string, unknown>): InvoiceListFilters => {
    const sort = Number(search.sort);
    const page = Number(search.page);
    return {
      q: typeof search.q === 'string' ? search.q : DEFAULT_INVOICE_LIST_FILTERS.q,
      status: statusFromSearch(search),
      businessUnitId:
        typeof search.businessUnitId === 'string' && search.businessUnitId
          ? search.businessUnitId
          : DEFAULT_INVOICE_LIST_FILTERS.businessUnitId,
      sort:
        Number.isFinite(sort) && sort >= 0
          ? Math.min(Math.floor(sort), 7)
          : DEFAULT_INVOICE_LIST_FILTERS.sort,
      dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
      dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
      invoiceNumber:
        typeof search.invoiceNumber === 'string'
          ? search.invoiceNumber
          : DEFAULT_INVOICE_LIST_FILTERS.invoiceNumber,
      totalOp: NUMBER_FILTER_OPS.includes(search.totalOp as InvoiceListFilters['totalOp'])
        ? (search.totalOp as InvoiceListFilters['totalOp'])
        : DEFAULT_INVOICE_LIST_FILTERS.totalOp,
      totalValue:
        typeof search.totalValue === 'string'
          ? search.totalValue
          : DEFAULT_INVOICE_LIST_FILTERS.totalValue,
      balanceOp: NUMBER_FILTER_OPS.includes(search.balanceOp as InvoiceListFilters['balanceOp'])
        ? (search.balanceOp as InvoiceListFilters['balanceOp'])
        : DEFAULT_INVOICE_LIST_FILTERS.balanceOp,
      balanceValue:
        typeof search.balanceValue === 'string'
          ? search.balanceValue
          : DEFAULT_INVOICE_LIST_FILTERS.balanceValue,
      dueFrom: typeof search.dueFrom === 'string' ? search.dueFrom : undefined,
      dueTo: typeof search.dueTo === 'string' ? search.dueTo : undefined,
      customerEmail:
        typeof search.customerEmail === 'string'
          ? search.customerEmail
          : DEFAULT_INVOICE_LIST_FILTERS.customerEmail,
      customerPhone:
        typeof search.customerPhone === 'string'
          ? search.customerPhone
          : DEFAULT_INVOICE_LIST_FILTERS.customerPhone,
      page:
        Number.isFinite(page) && page > 0 ? Math.floor(page) : DEFAULT_INVOICE_LIST_FILTERS.page,
    };
  },
  component: InvoicesLayout,
});

function cleanInvoiceSearch(next: Record<string, unknown>): InvoiceListFilters {
  // Read into `status` by validateSearch; the URL keeps only the canonical name.
  delete next.displayStatus;
  if (!next.q) delete next.q;
  if (next.status === DEFAULT_INVOICE_LIST_FILTERS.status) delete next.status;
  if (next.businessUnitId === DEFAULT_INVOICE_LIST_FILTERS.businessUnitId)
    delete next.businessUnitId;
  if (next.sort === DEFAULT_INVOICE_LIST_FILTERS.sort) delete next.sort;
  if (!next.dateFrom) delete next.dateFrom;
  if (!next.dateTo) delete next.dateTo;
  if (!next.invoiceNumber) delete next.invoiceNumber;
  if (next.totalOp === DEFAULT_INVOICE_LIST_FILTERS.totalOp) delete next.totalOp;
  if (!next.totalValue) delete next.totalValue;
  if (next.balanceOp === DEFAULT_INVOICE_LIST_FILTERS.balanceOp) delete next.balanceOp;
  if (!next.balanceValue) delete next.balanceValue;
  if (!next.dueFrom) delete next.dueFrom;
  if (!next.dueTo) delete next.dueTo;
  if (!next.customerEmail) delete next.customerEmail;
  if (!next.customerPhone) delete next.customerPhone;
  if (next.page === DEFAULT_INVOICE_LIST_FILTERS.page) delete next.page;
  return next as unknown as InvoiceListFilters;
}

function InvoicesLayout() {
  const router = useRouter();
  const isMobile = useIsMobile();
  const filters = Route.useSearch();
  const { location } = useRouterState();
  const {
    selectedId,
    isFormMode,
    hasChildRoute,
    isIndexRoute,
    preservedSearch: listSearch,
  } = useEntityRouteState({
    basePath: '/dashboard/invoices',
    search: filters,
    cleanSearch: cleanInvoiceSearch,
  });

  // A `?displayStatus=` link: write the status it named as `status=` and drop
  // the alias, so the address says what the page is filtering by. Idempotent on
  // the current search, so it cannot undo a filter change made meanwhile.
  const hasDisplayStatusAlias = Object.prototype.hasOwnProperty.call(
    location.search,
    'displayStatus',
  );
  useEffect(() => {
    if (!hasDisplayStatusAlias) return;
    void router.navigate({
      search: (prev) => {
        const raw = { ...(prev as Record<string, unknown>) };
        if (!Object.prototype.hasOwnProperty.call(raw, 'displayStatus')) return prev as never;
        return cleanInvoiceSearch({ ...raw, status: statusFromSearch(raw) }) as never;
      },
      replace: true,
    });
  }, [hasDisplayStatusAlias, router]);

  // Reports render full-page (their own shell).
  const isReportsRoute = /\/dashboard\/invoices\/reports\/?$/.test(location.pathname);
  if (isReportsRoute) return <Outlet />;

  function handleFiltersChange(patch: Partial<InvoiceListFilters>) {
    void router.navigate({
      search: (prev) => {
        const next = { ...prev, ...patch } as Record<string, unknown>;
        return cleanInvoiceSearch(next) as never;
      },
      replace: true,
    });
  }

  function handleNewInvoice() {
    void router.navigate({ to: '/dashboard/invoices/new', search: listSearch as never });
  }

  return (
    <WorkspaceShell hideHeader={isFormMode}>
      <InvoicesNewButtonSlot
        hidden={isFormMode || (isMobile && hasChildRoute)}
        onClick={handleNewInvoice}
      />
      <EntityIndexLayout
        title="Invoices"
        storageKey="invoices-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() =>
          void router.navigate({ to: '/dashboard/invoices', search: listSearch })
        }
        splitWidthClass="w-[360px]"
        tableSheetWidthClass={
          isFormMode ? 'w-[95vw] sm:max-w-[1200px]' : 'w-[720px] sm:max-w-[720px]'
        }
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        hideListInSplit={isFormMode}
        splitList={() => (
          <div className="flex h-full flex-col">
            <InvoiceListPanel
              selectedId={selectedId}
              onSelect={(invoice) =>
                void router.navigate({
                  to: '/dashboard/invoices/$id',
                  params: { id: invoice.id },
                  search: listSearch as never,
                })
              }
              onClearSelection={() =>
                void router.navigate({ to: '/dashboard/invoices', search: listSearch })
              }
              compact
              filters={filters}
              onFiltersChange={handleFiltersChange}
            />
          </div>
        )}
        splitDetail={<Outlet />}
        tableView={({ openInSheet }) => (
          <InvoiceTableView
            onOpenInvoice={(invoiceId) =>
              openInSheet(
                () =>
                  void router.navigate({
                    to: '/dashboard/invoices/$id',
                    params: { id: invoiceId },
                    search: listSearch as never,
                  }),
              )
            }
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="md:hidden h-full flex flex-col">
            {isIndexRoute ? (
              <InvoiceListPanel
                selectedId={selectedId}
                onSelect={(invoice) =>
                  void router.navigate({
                    to: '/dashboard/invoices/$id',
                    params: { id: invoice.id },
                    search: listSearch as never,
                  })
                }
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            ) : (
              <Outlet />
            )}
          </div>
        }
      />
    </WorkspaceShell>
  );
}

function InvoicesNewButtonSlot({ hidden, onClick }: { hidden: boolean; onClick: () => void }) {
  const canViewReports = useHasPermission('invoices:view');
  const pricingHidden = useIsFieldHidden('PRICING');

  const newButton = useMemo(() => {
    if (hidden || pricingHidden) return null;
    return (
      <Button size="sm" onClick={onClick}>
        <Plus size={14} className="mr-1.5" />
        New invoice
      </Button>
    );
  }, [hidden, onClick, pricingHidden]);

  const toolbarActions = useMemo(() => {
    if (hidden || !canViewReports || pricingHidden) return null;
    return (
      <Button variant="outline" size="sm" title="AR reports" asChild>
        <Link to="/dashboard/invoices/reports" search={(prev) => prev as never}>
          <BarChart3 size={14} />
          Reports
        </Link>
      </Button>
    );
  }, [hidden, canViewReports, pricingHidden]);

  useWorkspaceHeader({ newButton, toolbarActions }, [newButton, toolbarActions]);
  return null;
}
